import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type LocationState = {
  pathname: string;
  search: string;
  hash: string;
};

type NavigateOptions = {
  replace?: boolean;
};

type RouterContextValue = {
  location: LocationState;
  navigate: (to: string | number, options?: NavigateOptions) => void;
};

const RouterContext = createContext<RouterContextValue | null>(null);
const ParamsContext = createContext<Record<string, string | undefined>>({});

const decodePathname = (pathname: string) =>
  pathname
    .split('/')
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    })
    .join('/');

const readLocation = (): LocationState => ({
  pathname: decodePathname(window.location.pathname || '/'),
  search: window.location.search || '',
  hash: window.location.hash || '',
});

const getHistoryIndex = () => {
  const state = window.history.state as { idx?: number } | null;
  return Number(state?.idx ?? 0);
};

const mergeHistoryState = (idx: number) => {
  const state = window.history.state;
  return {
    ...(state && typeof state === 'object' ? state : {}),
    idx,
  };
};

export const RouterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<LocationState>(() => readLocation());

  useEffect(() => {
    if (!window.history.state || typeof window.history.state.idx !== 'number') {
      window.history.replaceState(mergeHistoryState(0), '', window.location.href);
    }

    const syncLocation = () => setLocation(readLocation());

    window.addEventListener('popstate', syncLocation);
    window.addEventListener('djeliormor:navigation', syncLocation);

    return () => {
      window.removeEventListener('popstate', syncLocation);
      window.removeEventListener('djeliormor:navigation', syncLocation);
    };
  }, []);

  const navigate = useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      window.history.go(to);
      return;
    }

    const url = new URL(to, window.location.origin);
    const next = `${url.pathname}${url.search}${url.hash}`;
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (next !== current) {
      const nextIndex = options?.replace ? getHistoryIndex() : getHistoryIndex() + 1;
      const nextState = mergeHistoryState(nextIndex);

      if (options?.replace) {
        window.history.replaceState(nextState, '', next);
      } else {
        window.history.pushState(nextState, '', next);
      }
    }

    window.dispatchEvent(new Event('djeliormor:navigation'));
  }, []);

  const value = useMemo(
    () => ({
      location,
      navigate,
    }),
    [location, navigate],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const ParamsProvider: React.FC<{
  params?: Record<string, string | undefined>;
  children: React.ReactNode;
}> = ({ params = {}, children }) => (
  <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>
);

export function useLocation(): LocationState {
  return useContext(RouterContext)?.location ?? readLocation();
}

export function useNavigate(): RouterContextValue['navigate'] {
  const context = useContext(RouterContext);

  return useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (context) {
        context.navigate(to, options);
        return;
      }

      if (typeof to === 'number') {
        window.history.go(to);
        return;
      }

      if (options?.replace) window.location.replace(to);
      else window.location.assign(to);
    },
    [context],
  );
}

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): Partial<T> {
  return useContext(ParamsContext) as Partial<T>;
}

type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: string;
  replace?: boolean;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, replace, onClick, target, children, ...props }, ref) => {
    const navigate = useNavigate();

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        target ||
        event.metaKey ||
        event.altKey ||
        event.ctrlKey ||
        event.shiftKey
      ) {
        return;
      }

      event.preventDefault();
      navigate(to, { replace });
    };

    return (
      <a ref={ref} href={to} target={target} onClick={handleClick} {...props}>
        {children}
      </a>
    );
  },
);

Link.displayName = 'Link';

export const Navigate: React.FC<{ to: string; replace?: boolean }> = ({ to, replace }) => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, replace, to]);

  return null;
};
